// components/layout/Sidebar.tsx
import React from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import {
    LayoutDashboard,
    Home,
    Users,
    BookOpen,
    Target,
    Building2,
    Building,
    Briefcase,
    GraduationCap,
    Mail,
    ChevronLeft,
    ChevronRight,
    Library
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useLayoutMode } from '@/components/layout/layoutConfig';

// Full wordmark (mark + "Nexilink"); shown when the rail has room.
const LOGO_FULL_SRC = 'https://cdn.builder.io/api/v1/image/assets/89bc6265b1a84995b7b85b1453a49e93/e937596a79e27aedd1ca37344ef2209cd6e8c5da2cc900e6c0f5467b6176325e?apiKey=89bc6265b1a84995b7b85b1453a49e93&';
// Mark only (no text); shown in the desktop-collapsed rail.
const LOGO_MARK_SRC = '/images/nexilink-mark.png';

interface SidebarProps {
    isOpen: boolean;
    isCollapsed: boolean;
    onClose: () => void;
    onToggleCollapse: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen, isCollapsed, onClose, onToggleCollapse }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useAuth();
    // Sidebar floats as a card in both island modes; flush only in classic.
    const island = useLayoutMode() !== 'classic';

    // Navigation items based on role
    const getNavigationItems = () => {
        if (user?.role === 'admin') {
            return {
                main: [
                    // #358 — six entries became four. "Översikt" is now "Hem" (a
                    // queue, not counters); "Enheter" is gone because a unit only
                    // exists inside an organisation and that page already manages
                    // them; "Kundetablering" is gone because creating something is
                    // an action (a button on Organisationer), not a destination.
                    { icon: Home, label: 'Hem', path: '/dashboard', disabled: false },
                    { icon: Building2, label: 'Organisationer', path: '/dashboard/organisations', disabled: false },
                    { icon: Users, label: 'Användare', path: '/dashboard/users', disabled: false },
                    { icon: Mail, label: 'Kontaktförfrågningar', path: '/dashboard/contacts', disabled: false }
                ],
                // "Rapporter" / "Support" hidden for now — will return when items are real.
                other: [] as Array<{ icon: typeof LayoutDashboard; label: string; path: string; disabled: boolean }>,
            };
        }

        if (user?.role === 'employer') {
            return {
                // Översikt hidden for now — it duplicated "Mina Behov" after declutter.
                // Employer's /dashboard redirects to /dashboard/needs (see App.tsx
                // DashboardIndex). Re-add here when a real overview is built.
                // Primary workflow areas (Profile moved to the account menu).
                main: [
                    { icon: Home, label: 'Hem', path: '/dashboard', disabled: false },
                    { icon: Briefcase, label: 'Rekrytering', path: '/dashboard/needs', disabled: false },
                    { icon: Users, label: 'Medarbetare', path: '/dashboard/employees', disabled: false, activeWhen: ['/dashboard/invitations'] },
                    { icon: BookOpen, label: 'Moduler', path: '/dashboard/courses', disabled: false },
                    { icon: GraduationCap, label: 'Onboarding', path: '/dashboard/onboarding', disabled: false },
                ],
                // Secondary tools ("Verktyg"). Inbjudningar now lives as a tab inside Medarbetare.
                other: [
                    { icon: Library, label: 'Bibliotek', path: '/dashboard/library', disabled: false },
                    // Chattar HIDDEN — the global "Fråga Link" launcher (LinkWorkspace)
                    // now owns chat + history. Route /dashboard/chat (ChatPage) is kept
                    // intact and reachable directly, so this is trivially revertible:
                    // uncomment to bring the nav item back.
                    // { icon: MessageSquare, label: 'Chattar', path: '/dashboard/chat', disabled: false },
                ],
            };
        }

        // Candidate
        return {
            // Primary flow: Jobb (find work) → Min onboarding (once hired). Profile is
            // in the account menu; applications/invitations/watches are tabs in Jobb;
            // Min arbetsplats is a tab in Min onboarding.
            main: [
                { icon: Home, label: 'Hem', path: '/dashboard', disabled: false },
                { icon: Briefcase, label: 'Jobb', path: '/dashboard/jobs', disabled: false, activeWhen: ['/dashboard/my-applications', '/dashboard/bevakningar', '/dashboard/my-invitations'] },
                // Moduler = the learning hub (onboarding section appears after hire +
                // platform courses). Replaces the old "Min Onboarding" + "Mina Moduler".
                { icon: BookOpen, label: 'Moduler', path: '/dashboard/moduler', disabled: false, activeWhen: ['/dashboard/my-onboarding', '/dashboard/modules'] },
            ],
            other: [] as Array<{ icon: typeof LayoutDashboard; label: string; path: string; disabled: boolean }>,
        };
    };

    const navigation = getNavigationItems();
    const isActive = (path: string) => {
        // Exact match for /dashboard (Översikt), prefix match for other paths
        if (path === '/dashboard') {
            return location.pathname === '/dashboard';
        }
        return location.pathname === path || location.pathname.startsWith(path + '/');
    };
    // An area's nav item stays highlighted across its sub-route tabs (e.g. "Jobb"
    // while on /my-applications). `activeWhen` lists those extra paths.
    const isItemActive = (item: { path: string; activeWhen?: string[] }) =>
        isActive(item.path) || (item.activeWhen ?? []).some((p) => isActive(p));

    return (
        <>
            {/* Mobile Overlay */}
            {/* D3 — nav chrome sits in a band BELOW the floating Link window (z-40)
                and the z-50 modal layer, but ABOVE page content + TopHeader (z-30):
                backdrop z-[36] (still dims TopHeader on mobile), panel z-[38]. This
                lets a wide/large windowed Link float over the sidebar instead of
                being clipped behind it, while real modals still cover everything. */}
            {isOpen && (
                <div
                    className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[36] lg:hidden"
                    onClick={onClose}
                />
            )}

            {/* Sidebar - Responsive width */}
            <aside
                className={cn(
                    'fixed bg-white z-[38] transition-all duration-300 flex flex-col',
                    island
                        // Floating "island": detached with gaps on all sides, a rounded
                        // card body + subtle shadow (height implied by top+bottom insets).
                        ? 'top-3 left-3 bottom-3 rounded-2xl border-2 border-gray-100 shadow-sm overflow-hidden'
                        // Classic: flush to the edges, full height, right border.
                        : 'top-0 left-0 h-screen border-r-2 border-gray-100 overflow-x-hidden overflow-y-auto',
                    // Mobile: Full overlay width
                    'w-72',
                    // Desktop: Collapsed or expanded
                    isCollapsed ? 'lg:w-20' : 'lg:w-72',
                    // Mobile: Slide in/out (island uses 120% to clear its left gap)
                    isOpen
                        ? 'translate-x-0'
                        : (island ? '-translate-x-[120%] lg:translate-x-0' : '-translate-x-full lg:translate-x-0'),
                )}
            >
                {/* Logo Section — matches landing page header */}
                {/* <div className="flex items-center justify-between h-16 sm:h-[72px] px-4 sm:px-6 lg:px-8"> */}
                <div className={cn(
                    "h-16 sm:h-[72px] flex items-center justify-between transition-all duration-300",
                    isCollapsed ? "lg:justify-center lg:px-3" : "px-4 sm:px-6 lg:px-8",
                    "px-4 sm:px-6"
                )}>

                    <Link to="/" className="flex-shrink-0">
                        {isCollapsed ? (
                            <>
                                {/* Desktop-collapsed rail → mark only (no text) */}
                                <img
                                    src={LOGO_MARK_SRC}
                                    alt="Nexilink"
                                    className="hidden lg:block h-8 w-auto object-contain flex-shrink-0"
                                />
                                {/* Mobile overlay is full-width → keep the wordmark */}
                                <img
                                    loading="lazy"
                                    src={LOGO_FULL_SRC}
                                    alt="Nexilink"
                                    className="lg:hidden h-8 w-auto object-contain flex-shrink-0"
                                />
                            </>
                        ) : (
                            <img
                                loading="lazy"
                                src={LOGO_FULL_SRC}
                                alt="Nexilink"
                                className="h-8 sm:h-10 w-auto object-contain flex-shrink-0"
                            />
                        )}
                    </Link>

                    {/* Collapse Toggle Button - Desktop Only - Only when NOT collapsed */}
                    {!isCollapsed && (
                        <button
                            onClick={onToggleCollapse}
                            className="hidden lg:flex items-center justify-center w-8 h-8 text-gray-600 hover:bg-gray-200 rounded-lg transition-colors flex-shrink-0"
                            title="Minimera sidofält"
                        >
                            <ChevronLeft className="w-5 h-5" />
                        </button>
                    )}
                </div>

                {/* Collapse Toggle Button - Centered when collapsed */}
                {isCollapsed && (
                    <div className="hidden lg:flex justify-center px-2">
                        <button
                            onClick={onToggleCollapse}
                            className="w-8 h-8 flex items-center justify-center text-gray-600 hover:bg-gray-200 rounded-lg transition-colors"
                            title="Expandera sidofält"
                        >
                            <ChevronRight className="w-5 h-5" />
                        </button>
                    </div>
                )}

                {/* Navigation */}
                <nav className="flex-1 overflow-y-auto overflow-x-hidden py-6">
                    {/* MAIN MENU Section */}
                    {!isCollapsed && (
                        <div className="px-4 mb-2 lg:block hidden">
                            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider px-3 whitespace-nowrap">
                                Huvudmeny
                            </p>
                        </div>
                    )}
                    {/* Always show on mobile */}
                    <div className="px-4 mb-2 lg:hidden block">
                        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider px-3 whitespace-nowrap">
                            Huvudmeny
                        </p>
                    </div>

                    <div className={cn(
                        "space-y-2 mb-6 transition-all duration-300",
                        isCollapsed ? "lg:px-2" : "px-4",
                        "px-4"
                    )}>
                        {navigation.main.map((item) => {
                            const Icon = item.icon;
                            const active = isItemActive(item);
                            const disabled = 'disabled' in item && item.disabled;

                            return (
                                <button
                                    key={item.path}
                                    onClick={() => {
                                        if (disabled) return;
                                        navigate(item.path);
                                        onClose(); // Close mobile menu on navigation
                                    }}
                                    disabled={disabled}
                                    className={cn(
                                        'w-full flex items-center gap-3 rounded-xl transition-all duration-200',
                                        'group relative',
                                        isCollapsed ? 'lg:justify-center lg:px-0 lg:py-3' : 'px-4 py-3',
                                        'px-4 py-3',
                                        disabled
                                            ? 'text-gray-400 cursor-not-allowed'
                                            : active
                                                ? 'bg-gray-200 font-semibold'
                                                : 'text-gray-700 hover:bg-gray-100'
                                    )}
                                    title={isCollapsed ? item.label : undefined}
                                >
                                    <Icon className={cn(
                                        "w-5 h-5 flex-shrink-0",
                                        disabled ? "text-gray-400" : active && "text-[#1A4D4F]"
                                    )} />
                                    {!isCollapsed && (
                                        <span className="lg:block hidden whitespace-nowrap">{item.label}</span>
                                    )}
                                    {/* Always show on mobile */}
                                    <span className="lg:hidden block whitespace-nowrap">{item.label}</span>

                                    {/* Tooltip for collapsed state on desktop */}
                                    {isCollapsed && (
                                        <div className="hidden lg:block absolute left-full ml-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-50 transition-opacity">
                                            {item.label}
                                        </div>
                                    )}
                                </button>
                            );
                        })}
                    </div>

                    {/* REPORT & OTHERS Section — only rendered when there are items */}
                    {navigation.other.length > 0 && (
                        <>
                            {!isCollapsed ? (
                                <div className="px-4 mb-2 lg:block hidden">
                                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider px-3 whitespace-nowrap">
                                        Verktyg
                                    </p>
                                </div>
                            ) : (
                                <div className="px-4 mb-4 lg:block hidden">
                                    <hr className="bg-gray-200 h-0.5" />
                                </div>
                            )}
                            {/* Always show on mobile */}
                            <div className="px-4 mb-2 lg:hidden block">
                                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider px-3 whitespace-nowrap">
                                    Verktyg
                                </p>
                            </div>
                        </>
                    )}

                    {navigation.other.length > 0 && (
                    <div className={cn(
                        "space-y-2 transition-all duration-300",
                        isCollapsed ? "lg:px-2" : "px-4",
                        "px-4"
                    )}>
                        {navigation.other.map((item) => {
                            const Icon = item.icon;
                            const active = isItemActive(item);
                            const disabled = 'disabled' in item && item.disabled;

                            return (
                                <button
                                    key={item.path}
                                    onClick={() => {
                                        if (disabled) return;
                                        navigate(item.path);
                                        onClose();
                                    }}
                                    disabled={disabled}
                                    className={cn(
                                        'w-full flex items-center gap-3 rounded-xl transition-all duration-200',
                                        'group relative',
                                        isCollapsed ? 'lg:justify-center lg:px-0 lg:py-3' : 'px-4 py-3',
                                        'px-4 py-3',
                                        disabled
                                            ? 'text-gray-400 cursor-not-allowed'
                                            : active
                                                ? 'bg-gray-200 font-semibold'
                                                : 'text-gray-700 hover:bg-gray-100'
                                    )}
                                    title={isCollapsed ? item.label : undefined}
                                >
                                    <Icon className={cn(
                                        "w-5 h-5 flex-shrink-0",
                                        disabled ? "text-gray-400" : active && "text-[#1A4D4F]"
                                    )} />
                                    {!isCollapsed && (
                                        <span className="lg:block hidden whitespace-nowrap">{item.label}</span>
                                    )}
                                    {/* Always show on mobile */}
                                    <span className="lg:hidden block">{item.label}</span>

                                    {/* Tooltip for collapsed state on desktop */}
                                    {isCollapsed && (
                                        <div className="hidden lg:block absolute left-full ml-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-50 transition-opacity">
                                            {item.label}
                                        </div>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                    )}
                </nav>
            </aside>
        </>
    );
};

export default Sidebar;